import React, { useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { Checkbox } from "@/components/wpl/checkbox/src";
import { DropdownSelector } from "@/components/wpl/dropdown/src";
import { TextInput } from "@/components/wpl/text-input/src";
import { wacToken } from "@/libraries/design/foundations";

import {
    PRODUCT_OPTIONS,
    RECOMMENDATION_FIELDS,
    SEGMENT_CONTROL_CONFIGS,
    SegmentControlConfig
} from "./personalization-segment-config";
import {
    UserSegmentTypes,
    ProductRecommendationOptions
} from "./use-user-segments";

export interface PersonalizationUserSegmentValues {
    [UserSegmentTypes.LOGIN_STATUS]?: string;
    [UserSegmentTypes.GEOLOCATION]?: string;
    [UserSegmentTypes.JOB_FUNCTION]?: string;
    [UserSegmentTypes.DOMAIN_TYPE]?: string;
    [UserSegmentTypes.OWNED_PRODUCTS]?: string[];
    [UserSegmentTypes.CLUSTER_TAG]?: string;
    [UserSegmentTypes.FIRST_RECOMMENDATION]?: ProductRecommendationOptions;
    [UserSegmentTypes.SECOND_RECOMMENDATION]?: ProductRecommendationOptions;
    [UserSegmentTypes.THIRD_RECOMMENDATION]?: ProductRecommendationOptions;
    [UserSegmentTypes.FOURTH_RECOMMENDATION]?: ProductRecommendationOptions;
    [UserSegmentTypes.COLLECTION_OPTIONS]?: string[];
    useOverrides: boolean;
    enrollInExperiment: boolean;
}

const LABEL_STYLES = css({
    display: "block",
    color: wacToken("color.neutral.dark200"),
    fontSize: "0.75rem",
    lineHeight: "1.1",
    marginBlockEnd: token("space.050"),
    textAlign: "left"
});

const isUniformPersonalizationEditMode = () =>
    typeof window !== "undefined" &&
    window.location.pathname.includes("/uniform-preview");

interface PersonalizationOverrideControlsProps {
    values: PersonalizationUserSegmentValues;
    setValues: React.Dispatch<
        React.SetStateAction<PersonalizationUserSegmentValues>
    >;
    renderMessage: string;
    isContainerHovered: boolean;
    requiredSegmentTypes?: UserSegmentTypes[];
    isPinned?: boolean;
    setIsPinned?: React.Dispatch<React.SetStateAction<boolean>>;
    setIsActive?: React.Dispatch<React.SetStateAction<boolean>>;
}

const PersonalizationControlPanel = ({
    values,
    handleSelectChange,
    requiredSegmentTypes
}: {
    values: PersonalizationUserSegmentValues;
    handleSelectChange: (
        field: keyof PersonalizationUserSegmentValues,
        value: string | boolean | string[]
    ) => void;
    requiredSegmentTypes: UserSegmentTypes[];
}) => {
    const isSegmentControlVisible = (segmentType: UserSegmentTypes) =>
        requiredSegmentTypes.includes(segmentType);
    const getMultiSelectValues = (segmentType: UserSegmentTypes): string[] => {
        const segmentValues = values[segmentType];
        if (Array.isArray(segmentValues)) {
            return segmentValues;
        }
        if (typeof segmentValues === "string" && segmentValues.length > 0) {
            return [segmentValues];
        }
        return [];
    };

    const renderSegmentControl = (control: SegmentControlConfig) => {
        const segmentValue = values[control.segmentType];
        switch (control.ux) {
            case "dropdown": {
                const selectedValue =
                    typeof segmentValue === "string" ? segmentValue : "";
                const selectedLabel =
                    control.getSelectedLabel?.(
                        selectedValue,
                        control.options
                    ) ?? control.defaultLabel;
                return (
                    <DropdownSelector
                        name={control.segmentType}
                        id={control.segmentType}
                        label={control.label}
                        options={control.options}
                        defaultValue={{
                            value: selectedValue,
                            label: selectedLabel
                        }}
                        onChangeHandler={(value) => {
                            handleSelectChange(
                                control.segmentType,
                                value as string
                            );
                        }}
                    />
                );
            }
            case "textbox": {
                const textboxValue =
                    typeof segmentValue === "string" ? segmentValue : "";
                const textboxPlaceholder: string = control.placeholder ?? "";

                if (control.segmentType === UserSegmentTypes.CLUSTER_TAG) {
                    return (
                        <>
                            <label
                                css={LABEL_STYLES}
                                htmlFor={control.segmentType}
                            >
                                {control.label}
                            </label>
                            <input
                                id={control.segmentType}
                                data-testid={control.segmentType}
                                css={[css({ width: "100%" })]}
                                type="text"
                                value={textboxValue}
                                onChange={(event) => {
                                    const nextValue = control.transformValue
                                        ? control.transformValue(
                                              event.target.value
                                          )
                                        : event.target.value;
                                    handleSelectChange(
                                        control.segmentType,
                                        nextValue
                                    );
                                }}
                            />
                        </>
                    );
                }

                return (
                    <TextInput
                        id={control.segmentType}
                        name={control.segmentType}
                        label={control.label}
                        placeholder={textboxPlaceholder}
                        value={textboxValue}
                        onChangeHandler={(value) => {
                            const nextValue = control.transformValue
                                ? control.transformValue(value as string)
                                : (value as string);
                            handleSelectChange(control.segmentType, nextValue);
                        }}
                    />
                );
            }
            case "multi-select": {
                const selectedValues = getMultiSelectValues(
                    control.segmentType
                );

                return (
                    <>
                        <label css={LABEL_STYLES} htmlFor={control.segmentType}>
                            {control.label}
                        </label>
                        <div
                            css={css({
                                display: "flex",
                                flexWrap: "wrap",
                                gap: token("space.050"),
                                padding: token("space.100"),
                                border: `1px solid ${wacToken(
                                    "color.neutral.dark700"
                                )}`,
                                borderRadius: "4px",
                                minHeight: "40px",
                                alignItems: "center"
                            })}
                        >
                            {control.options.map((option) => {
                                const isSelected = selectedValues.includes(
                                    option.value
                                );
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => {
                                            const updatedProducts = isSelected
                                                ? selectedValues.filter(
                                                      (product) =>
                                                          product !==
                                                          option.value
                                                  )
                                                : [
                                                      ...selectedValues,
                                                      option.value
                                                  ];

                                            handleSelectChange(
                                                control.segmentType,
                                                updatedProducts
                                            );
                                        }}
                                        css={css({
                                            "padding": `${token(
                                                "space.050"
                                            )} ${token("space.100")}`,
                                            "backgroundColor": isSelected
                                                ? wacToken(
                                                      "color.extended.blue300"
                                                  )
                                                : wacToken(
                                                      "color.neutral.light200"
                                                  ),
                                            "color": isSelected
                                                ? wacToken(
                                                      "color.neutral.light0"
                                                  )
                                                : wacToken(
                                                      "color.neutral.dark200"
                                                  ),
                                            "border": "none",
                                            "borderRadius": "4px",
                                            "cursor": "pointer",
                                            "fontSize": "0.875rem",
                                            ":hover": {
                                                backgroundColor: isSelected
                                                    ? wacToken(
                                                          "color.extended.blue400"
                                                      )
                                                    : wacToken(
                                                          "color.neutral.light300"
                                                      )
                                            }
                                        })}
                                    >
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                    </>
                );
            }
            default:
                return null;
        }
    };

    return (
        <div
            css={css({
                display: "flex",
                flexDirection: "column",
                gap: token("space.100")
            })}
        >
            <div
                data-personalization-toggle="enrollInExperiment"
                onPointerDownCapture={(event) => {
                    event.stopPropagation();
                }}
                onMouseDown={(event) => {
                    event.stopPropagation();
                }}
                onClick={(event) => {
                    event.stopPropagation();
                    if (isUniformPersonalizationEditMode()) {
                        return;
                    }
                    const target = event.target as EventTarget | null;
                    if (
                        target instanceof HTMLInputElement &&
                        target.type === "checkbox"
                    ) {
                        handleSelectChange(
                            "enrollInExperiment",
                            target.checked
                        );
                        return;
                    }
                    handleSelectChange(
                        "enrollInExperiment",
                        !values.enrollInExperiment
                    );
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelectChange(
                            "enrollInExperiment",
                            !values.enrollInExperiment
                        );
                    }
                }}
                role="button"
                tabIndex={0}
            >
                <Checkbox
                    id="enrollInExperiment"
                    name="enrollInExperiment"
                    value="enrollInExperiment"
                    label="Enroll in Experiment Treatment?"
                    checked={values.enrollInExperiment}
                />
            </div>
            <div
                data-personalization-toggle="useOverrides"
                onPointerDownCapture={(event) => {
                    event.stopPropagation();
                }}
                onMouseDown={(event) => {
                    event.stopPropagation();
                }}
                onClick={(event) => {
                    event.stopPropagation();
                    if (isUniformPersonalizationEditMode()) {
                        return;
                    }
                    const target = event.target as EventTarget | null;
                    if (
                        target instanceof HTMLInputElement &&
                        target.type === "checkbox"
                    ) {
                        handleSelectChange("useOverrides", target.checked);
                        return;
                    }
                    handleSelectChange("useOverrides", !values.useOverrides);
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelectChange(
                            "useOverrides",
                            !values.useOverrides
                        );
                    }
                }}
                role="button"
                tabIndex={0}
            >
                <Checkbox
                    id="useOverrides"
                    name="useOverrides"
                    value="useOverrides"
                    label="Use Overrides?"
                    checked={values.useOverrides}
                />
            </div>
            {SEGMENT_CONTROL_CONFIGS.filter(({ segmentType }) =>
                isSegmentControlVisible(segmentType)
            ).map((control) => (
                <div key={control.segmentType}>
                    {renderSegmentControl(control)}
                </div>
            ))}
            {RECOMMENDATION_FIELDS.filter(({ segmentType }) =>
                isSegmentControlVisible(segmentType)
            ).map(({ segmentType, label, defaultValue, defaultLabel }) => {
                const selectedValue = values[segmentType] || defaultValue;
                const selectedOption = PRODUCT_OPTIONS.find(
                    (opt) => opt.value === selectedValue
                );
                return (
                    <div key={segmentType}>
                        <DropdownSelector
                            name={segmentType}
                            id={segmentType}
                            label={label}
                            options={PRODUCT_OPTIONS}
                            defaultValue={{
                                value: selectedValue,
                                label: selectedOption?.label || defaultLabel
                            }}
                            onChangeHandler={(value) => {
                                handleSelectChange(
                                    segmentType,
                                    value as string
                                );
                            }}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export const PersonalizationOverrideControls = ({
    values,
    setValues,
    renderMessage,
    isContainerHovered,
    isPinned = false,
    setIsPinned = () => undefined,
    setIsActive = () => undefined,
    requiredSegmentTypes = Object.values(UserSegmentTypes) as UserSegmentTypes[]
}: PersonalizationOverrideControlsProps) => {
    const controlsRootRef = useRef<HTMLDivElement | null>(null);
    const skipNextDogEarClickRef = useRef(false);
    const valuesRef = useRef(values);
    const togglePinnedStateRef = useRef<() => void>(() => undefined);
    const [isPointerInside, setIsPointerInside] = useState(false);
    const [isFocusWithin, setIsFocusWithin] = useState(false);
    const isVisible = isPinned || isPointerInside || isFocusWithin;
    const isTriggerVisible = isContainerHovered || isPinned;
    const triggerTitleByPinned = {
        true: "Hide Personalization Controls",
        false: "Show Personalization Controls"
    } as const;
    const triggerIconByPinned = {
        true: "📌",
        false: "👥"
    } as const;

    const togglePinnedState = () => {
        setIsPinned((previousState) => {
            const nextPinnedState = !previousState;
            if (!nextPinnedState) {
                setIsPointerInside(false);
                setIsFocusWithin(false);
            }
            setIsActive(nextPinnedState);
            return nextPinnedState;
        });
    };

    const handleSelectChange = (
        field: keyof PersonalizationUserSegmentValues,
        value: string | boolean | string[]
    ) => {
        setValues((previousValues) => ({
            ...previousValues,
            [field]: value
        }));
    };

    valuesRef.current = values;
    togglePinnedStateRef.current = togglePinnedState;

    useEffect(() => {
        if (!isUniformPersonalizationEditMode()) {
            return;
        }

        const handleNativeTogglePointer = (event: Event) => {
            const controlsRoot = controlsRootRef.current;
            const target = event.target;
            if (!controlsRoot || !(target instanceof Element)) {
                return;
            }
            if (!controlsRoot.contains(target)) {
                return;
            }

            const dogEarRow = target.closest(
                '[data-personalization-dogear="toggle"]'
            );
            const enrollRow = target.closest(
                '[data-personalization-toggle="enrollInExperiment"]'
            );
            const overridesRow = target.closest(
                '[data-personalization-toggle="useOverrides"]'
            );
            if (!dogEarRow && !enrollRow && !overridesRow) {
                return;
            }

            event.stopPropagation();
            if (event.cancelable) {
                event.preventDefault();
            }

            if (dogEarRow) {
                skipNextDogEarClickRef.current = true;
                togglePinnedStateRef.current();
                return;
            }

            const currentValues = valuesRef.current;
            if (enrollRow) {
                const nextValue = !currentValues.enrollInExperiment;
                handleSelectChange("enrollInExperiment", nextValue);
                return;
            }

            const nextValue = !currentValues.useOverrides;
            handleSelectChange("useOverrides", nextValue);
        };

        document.addEventListener("pointerdown", handleNativeTogglePointer, {
            capture: true
        });
        document.addEventListener("mousedown", handleNativeTogglePointer, {
            capture: true
        });

        return () => {
            document.removeEventListener(
                "pointerdown",
                handleNativeTogglePointer,
                {
                    capture: true
                }
            );
            document.removeEventListener(
                "mousedown",
                handleNativeTogglePointer,
                {
                    capture: true
                }
            );
        };
    }, []);

    // Only show the dog-ear when container is hovered
    if (!isContainerHovered && !isPinned) {
        return null;
    }

    return (
        <div
            ref={controlsRootRef}
            css={css({
                position: "absolute",
                insetBlockStart: "-2px",
                insetInlineStart: "-2px",
                zIndex: 2147483001,
                pointerEvents: "none"
            })}
            data-testid="personalization-controls"
        >
            {/* Dog-ear Corner */}
            <div
                css={css({
                    position: "absolute",
                    insetBlockStart: 0,
                    insetInlineStart: 0,
                    zIndex: 2147483002,
                    width: "48px",
                    height: "48px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    opacity: Number(isTriggerVisible),
                    transform: ["scale(0.8)", "scale(1)"][
                        Number(isTriggerVisible)
                    ],
                    pointerEvents: "auto"
                })}
                data-personalization-dogear="toggle"
                title={
                    triggerTitleByPinned[String(isPinned) as "true" | "false"]
                }
                onMouseEnter={() => {
                    setIsPointerInside(true);
                    setIsActive(true);
                }}
                onPointerDownCapture={(event) => {
                    event.stopPropagation();
                }}
                onMouseDown={(event) => {
                    event.stopPropagation();
                }}
                onMouseLeave={() => {
                    setIsPointerInside(false);
                    if (!isPinned && !isFocusWithin) {
                        setIsActive(false);
                    }
                }}
                onFocus={() => {
                    setIsFocusWithin(true);
                    setIsActive(true);
                }}
                onBlur={() => {
                    setIsFocusWithin(false);
                    if (!isPinned && !isPointerInside) {
                        setIsActive(false);
                    }
                }}
                onClick={() => {
                    if (skipNextDogEarClickRef.current) {
                        skipNextDogEarClickRef.current = false;
                        return;
                    }
                    togglePinnedState();
                }}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        togglePinnedState();
                    }
                }}
                role="button"
                tabIndex={0}
                aria-pressed={isPinned}
            >
                {/* Rounded corner background */}
                <div
                    css={css({
                        "position": "absolute",
                        "insetBlockStart": 0,
                        "insetInlineStart": 0,
                        "width": "48px",
                        "height": "48px",
                        "backgroundColor": wacToken("color.extended.blue300"),
                        "borderRadius": `${wacToken(
                            "corner.radius.card"
                        )} 0 ${wacToken("corner.radius.card")} 0`,
                        "transition": "all 0.2s ease",
                        "boxShadow": `0 2px 4px ${wacToken(
                            "color.neutral.dark800"
                        )}`,
                        ":hover": {
                            backgroundColor: wacToken("color.extended.blue400")
                        }
                    })}
                />

                {/* Icon */}
                <div
                    css={css({
                        position: "absolute",
                        insetBlockStart: "50%",
                        insetInlineStart: "50%",
                        transform: "translate(-50%, -50%)",
                        color: wacToken("color.neutral.light0"),
                        fontSize: "16px",
                        lineHeight: 1,
                        pointerEvents: "none",
                        textShadow: `0 1px 2px ${wacToken(
                            "color.neutral.dark800"
                        )}`
                    })}
                >
                    {triggerIconByPinned[String(isPinned) as "true" | "false"]}
                </div>
            </div>

            {/* Control Panel */}
            {isVisible && (
                <div
                    css={css({
                        position: "absolute",
                        insetBlockStart: "48px",
                        insetInlineStart: "8px",
                        width: "260px",
                        maxHeight: "70vh",
                        zIndex: 2147483003,
                        padding: token("space.200"),
                        display: "flex",
                        flexDirection: "column",
                        gap: token("space.100"),
                        backgroundColor: wacToken("color.neutral.light0"),
                        borderRadius: wacToken("corner.radius.card"),
                        boxShadow: `0 4px 12px ${wacToken(
                            "color.neutral.dark800"
                        )}`,
                        border: `1px solid ${wacToken(
                            "color.neutral.dark700"
                        )}`,
                        pointerEvents: "auto"
                    })}
                    onMouseEnter={() => {
                        setIsPointerInside(true);
                        setIsActive(true);
                    }}
                    onMouseLeave={() => {
                        if (!isPinned) {
                            setIsPointerInside(false);
                            if (!isFocusWithin) {
                                setIsActive(false);
                            }
                        }
                    }}
                    onFocus={() => {
                        setIsFocusWithin(true);
                        setIsActive(true);
                    }}
                    onBlur={(event) => {
                        const relatedTarget =
                            event.relatedTarget as Node | null;
                        if (
                            relatedTarget &&
                            event.currentTarget.contains(relatedTarget)
                        ) {
                            return;
                        }
                        setIsFocusWithin(false);
                        if (!isPinned && !isPointerInside) {
                            setIsActive(false);
                        }
                    }}
                >
                    <div
                        css={css({
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: token("space.100")
                        })}
                    >
                        <h3
                            css={css({
                                margin: 0,
                                fontSize: wacToken("font.charlie.text"),
                                color: wacToken("color.neutral.dark200")
                            })}
                        >
                            Personalization Controls
                        </h3>
                    </div>

                    <div
                        css={css({
                            padding: token("space.075"),
                            fontSize: "0.75rem",
                            color: wacToken("color.neutral.dark300"),
                            backgroundColor: wacToken(
                                "color.extended.saffron100"
                            ),
                            borderRadius: "4px",
                            border: `1px solid ${wacToken(
                                "color.extended.saffron300"
                            )}`
                        })}
                    >
                        These controls and the blue border are only visible in
                        pre-production environments
                    </div>

                    <div
                        css={css({
                            overflowY: "auto",
                            maxHeight: "calc(70vh - 220px)",
                            paddingInlineEnd: token("space.050")
                        })}
                    >
                        <PersonalizationControlPanel
                            values={values}
                            handleSelectChange={handleSelectChange}
                            requiredSegmentTypes={requiredSegmentTypes}
                        />
                    </div>

                    <div
                        css={css({
                            padding: token("space.100"),
                            backgroundColor: wacToken("color.extended.blue100"),
                            color: wacToken("color.neutral.dark300")
                        })}
                    >
                        {renderMessage}
                    </div>
                </div>
            )}
        </div>
    );
};
